import React, { FC, useCallback, useMemo, useState } from 'react'
import { NoteDto } from '../../api-client';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import Notes from '../../components/ScheduleOverview/Notes/Notes';
import { UserContext } from '../../stores/User/User.provider';
import { observer } from 'mobx-react';
import { useQueryClient } from '@tanstack/react-query';
import { calendarKeys } from '../../stores/Calendar/calendar.keys';
import { AppointmentsOfAllTypesDto } from '../../api-client';
import { useAppointmentNotes } from '../../stores/Notes/notes.queries';
import { useCreateNote, useUpdateNote } from '../../stores/Notes/notes.mutations';
import { notesKeys } from '../../stores/Notes/notes.keys';
import useStore from "../../helpers/useStore";
import DialogDefaultAction from "../../atoms/DialogDefaultAction";

interface AppointmentNotesProps {
  appointmentId: string;
  showDialog: boolean;
  onClose?: () => void;
}

const AppointmentNotes:FC<AppointmentNotesProps> = observer(({appointmentId, showDialog, onClose}) => {
  const [showNotesDialog, setShowNotesDialog] = useState(showDialog);
  const [isDirty, setIsDirty] = useState(false);
  const { data: appointmentNotes = [] } = useAppointmentNotes(appointmentId, !!appointmentId);
  const sortedNotes = useMemo(() => {
    // Show every note that belongs to THIS appointment, oldest first.
    return [...appointmentNotes].sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return aTime - bTime;
    });
  }, [appointmentNotes]);
  const { mutateAsync: createNote } = useCreateNote();
  const { mutateAsync: updateNoteById } = useUpdateNote();
  const UserStore = useStore(UserContext);
  const { user } = UserStore;
  const queryClient = useQueryClient();

  /**
   * Updates the calendar cache to reflect the notes for the appointment
   */
  const updateAppointmentNotesCache = (appointmentId: string, updatedNotes: NoteDto[]) => {
    const hasUpdatedNotes = updatedNotes.length > 0;
    const participantMatchesAppointment = (participant: any) => {
      return (
        participant?.id === appointmentId ||
        participant?.appointmentId === appointmentId ||
        participant?.timeSlots?.some(
          (timeSlot: any) => timeSlot?.appointmentId === appointmentId || timeSlot?.id === appointmentId,
        )
      );
    };

    const updateAppointment = (appointment: any) => {
      let didUpdate = false;
      let nextAppointment = appointment;

      if (appointment.id === appointmentId) {
        didUpdate = true;
        nextAppointment = {
          ...nextAppointment,
          notes: updatedNotes,
          hasNotes: hasUpdatedNotes,
        };
      }

      if (appointment.participants && appointment.participants.length > 0) {
        const updatedParticipants = appointment.participants.map((participant: any) => {
          if (!participantMatchesAppointment(participant)) return participant;
          didUpdate = true;
          return {
            ...participant,
            notes: updatedNotes,
            hasNotes: hasUpdatedNotes,
          };
        });

        if (didUpdate) {
          nextAppointment = {
            ...nextAppointment,
            participants: updatedParticipants,
          };
        }
      }

      return didUpdate ? nextAppointment : appointment;
    };

    // We need to update all potential query caches that might contain this appointment
    // Since we don't know the exact date, we update all cached appointment queries
    queryClient.setQueriesData<AppointmentsOfAllTypesDto>(
      { queryKey: calendarKeys.appointmentsForDate.all() },
      (oldData) => {
        if (!oldData) return oldData;
        const updatedAppointments = oldData.appointments?.map(updateAppointment);
        const didUpdate = updatedAppointments?.some((appointment, index) => appointment !== oldData.appointments[index]);
        if (!didUpdate) return oldData;

        // Return new data object with updated appointments array
        return {
          ...oldData,
          appointments: updatedAppointments,
        };
      }
    );

    // Single therapist view stores appointments in the date-range cache,
    // so update it as well to refresh the note icon there.
    queryClient.setQueriesData<AppointmentsOfAllTypesDto>(
      { queryKey: calendarKeys.appointmentsForDateRange.all() },
      (oldData) => {
        if (!oldData) return oldData;
        const updatedAppointments = oldData.appointments?.map(updateAppointment);
        const didUpdate = updatedAppointments?.some((appointment, index) => appointment !== oldData.appointments[index]);
        if (!didUpdate) return oldData;

        return {
          ...oldData,
          appointments: updatedAppointments,
        };
      }
    );
  };

  const updateNote = async (index: number, updatedCard: NoteDto) => {
    const targetAppointmentId = (updatedCard.appointmentId as string) || appointmentId;
    if (targetAppointmentId) {
      updatedCard.appointmentId = targetAppointmentId;
      if(updatedCard.id){
        await updateNoteById({ noteId: updatedCard.id, userId: user?.id as string, note: updatedCard })
     }
     else{
      await createNote({ userId: user?.id as string, note: updatedCard });
     }

      // Read back the updated notes from the query cache after mutation invalidation
      const updatedNotes = queryClient.getQueryData<NoteDto[]>(
        notesKeys.appointmentNotes.detail(targetAppointmentId)
      ) ?? [...appointmentNotes.slice(0, index), updatedCard, ...appointmentNotes.slice(index + 1)];

      // Update the calendar cache with the new notes array
      updateAppointmentNotesCache(targetAppointmentId, updatedNotes);
    } else {
      // console.log("No selectedAppointmentId available.");
    }
  };

  const handleCloseNotesDialog = () => {
    setShowNotesDialog(false);
    onClose && onClose();
  };

  const handleDirtyChange = useCallback((dirty: boolean) => {
    setIsDirty(dirty);
  }, []);

  return (
     <Dialog
        open={showNotesDialog}
        onClose={handleCloseNotesDialog}
        aria-labelledby="form-dialog-title"
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: { boxShadow: "0 4px 8px 0 rgba(0, 0, 0, 0.2)" },
        }}
        slotProps={{
          backdrop: {
            invisible: true,
            style: { display: "none" },
          },
        }}
      >
         <DialogTitle sx={{ pb: 1 }}>Terminhinweise</DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
            <Notes
              notes={sortedNotes}
              setNotes={updateNote}
              maxVisibleNotes={sortedNotes.length}
              onDirtyChange={handleDirtyChange}
              autoFocus
              noteKind="Terminhinweis"
            />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNotesDialog}>{isDirty ? "Abbrechen" : "Schließen"}</Button>
        </DialogActions>
        <DialogDefaultAction onAction={handleCloseNotesDialog} disabled={!showNotesDialog} />
      </Dialog>
  )
})

export default AppointmentNotes